<script lang="ts" setup>
import type { CcxtMarket } from '@/services/types'
import { computed } from 'vue'
import { i18n } from '@/i18n'
import { calcDecimals } from '@/utils'
import TriggerButton from '@/components/TriggerButton.vue'
import { Button } from 'primevue'
import { useCexCexTwoLegsSettings } from '@/composable/settings/useCexCexTwoLegsSettings'

interface FormMarketData {
  cex: string
  asset: string
  dexPrice: number | null
  cexPriceBuy: number | null
  cexPriceSell: number | null
  spread: number | null
  backSpread: number | null
}

interface TwoLegsLegSpecificData {
  isProfilesPrepared: boolean
  preparingProfile: boolean
  creatingOrder: boolean
  fetchingBalance: boolean
  enabledProfiles: number[]
  canClosePositions: boolean
}

const props = defineProps<{
  marketData: FormMarketData
  formData: TwoLegsLegSpecificData
  cacheKeyPostfix: string
  minimized: boolean
  amount: number | null
  leverage: number | null
  slippage: number | null
  chain: string
  contract: string
  dexPriceBuy: number | null
  dexPriceSell: number | null
  market: CcxtMarket
}>()

const emit = defineEmits([
  'open-two-legs-short',
  'close-two-legs-short',
  'prepare-profile',
  'fetch-balance',
  'show-positions-modal',
])

const { t } = i18n.global
const { autoPrepare } = useCexCexTwoLegsSettings()

const priceRounding = computed(() => calcDecimals(props.market.precision.price))
const disablePrepare = computed(() => autoPrepare.value ? props.formData.isProfilesPrepared : false)
</script>

<template>
  <div>
    <!-- Minimized mode -->
    <div v-show="minimized" class="grid grid-cols-1 md:grid-cols-2 gap-2">
      <Button severity="danger" class="col-span-1" :disabled="!formData.canClosePositions ||
        formData.creatingOrder
        " @click="emit('close-two-legs-short')" variant="outlined" :loading="formData.creatingOrder" icon="pi pi-bolt"
        :label="$t('modal.createSwapOrders.closePositions')" fluid>
      </Button>
      <Button severity="success" class="col-span-1" :disabled="!Boolean(dexPriceBuy) ||
        !amount ||
        !leverage ||
        formData.preparingProfile ||
        formData.creatingOrder ||
        formData.enabledProfiles.length == 0
        " type="submit" variant="outlined" :loading="formData.creatingOrder" icon="pi pi-bolt"
        :label="$t('modal.createSwapOrders.openPositions')" fluid @click="emit('open-two-legs-short')">
      </Button>
    </div>

    <!-- Normal mode -->
    <div v-show="!minimized">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <!-- Prepare profile + Open positions row -->
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-3 col-span-2">
          <!-- Prepare profile -->
          <div class="grid grid-cols-4 col-span-2">
            <Button @click="autoPrepare = !autoPrepare" class="rounded-r-none border-r-0" severity="success"
              variant="outlined">
              <i class="pi" :class="{ 'pi-check-square': autoPrepare, 'pi-stop': !autoPrepare }"></i>
              <span>{{ $t("modal.createSwapOrders.auto") }}</span>
            </Button>
            <Button severity="success" class="col-span-3 rounded-l-none" variant="outlined"
              :disabled="!leverage || formData.preparingProfile || formData.enabledProfiles.length == 0 || disablePrepare"
              @click="emit('prepare-profile')" :loading="formData.preparingProfile"
              :label="$t('modal.createSwapOrders.prepareProfile')">
            </Button>
          </div>

          <!-- Open positions (two-legs short) -->
          <Button severity="success" class="col-span-2 lg:col-span-2" :disabled="!Boolean(dexPriceBuy) ||
            !amount ||
            !leverage ||
            formData.preparingProfile ||
            formData.creatingOrder ||
            formData.enabledProfiles.length == 0
            " type="submit" variant="outlined" :loading="formData.creatingOrder" icon="pi pi-bolt"
            :label="$t('modal.createSwapOrders.openPositions')" fluid @click="emit('open-two-legs-short')">
          </Button>
        </div>

        <!-- Close positions (two-legs) -->
        <Button severity="danger" class="col-span-2 lg:col-span-1" :disabled="!formData.canClosePositions ||
          formData.creatingOrder
          " @click="emit('close-two-legs-short')" variant="outlined" :loading="formData.creatingOrder"
          icon="pi pi-bolt" :label="$t('modal.createSwapOrders.closePositions')" fluid>
        </Button>

        <!-- Trigger -->
        <TriggerButton class="col-span-2 lg:col-span-1" :root="cacheKeyPostfix" cache-key="two-legs" :amount="amount">
        </TriggerButton>

        <!-- Positions -->
        <Button fluid class="col-span-2 lg:col-span-1" severity="success" variant="outlined"
          @click="emit('show-positions-modal')" :label="$t('modal.createSwapOrders.myPositions')">
        </Button>

        <!-- Balance -->
        <Button fluid icon="pi pi-money-bill" severity="success" :loading="formData.fetchingBalance"
          class="col-span-2 lg:col-span-1" :disabled="formData.enabledProfiles.length == 0 || formData.fetchingBalance"
          @click="emit('fetch-balance')" variant="outlined" :label="$t('modal.createSwapOrders.fetchBalance')">
        </Button>
      </div>
    </div>
  </div>
</template>
